/**
 * HannoNexus — Press & Investor Kit
 * Publicly shareable page with assets, fact sheet, bios and quick links.
 * URL: /press-kit
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download, ArrowRight, Mail, Phone, Linkedin, Calendar, Copy, CheckCheck,
  FileText, Image as ImageIcon, Flag, Zap, Users, Globe, Sparkles,
} from "lucide-react";
import { Navbar } from "@/components/hx/Navbar";
import { Footer } from "@/components/hx/Footer";
import { HannoMark } from "@/components/hanno/HannoMark";
import { CountryFlag } from "@/components/hanno/Flags";
import { toast } from "sonner";

const NEON = "#0A9B5F";
const CYAN = "#06B6D4";
const SOLAR = "#F59E0B";

const CONTACTS = {
  email: "admin@bsgotech.com",
  phone: "+49 176 84450554",
  linkedin: "https://www.linkedin.com/company/bsgotech",
};

const FACT_SHEET = [
  { icon: Zap, label: "Solar Instalado · Cabo Verde", value: "12.4 MWp", accent: SOLAR },
  { icon: Zap, label: "BESS Storage · Cabo Verde", value: "8 MWh", accent: NEON },
  { icon: Globe, label: "Sensores IoT · São Tomé", value: "128", accent: CYAN },
  { icon: Sparkles, label: "Pilares Operacionais", value: "5", accent: NEON },
  { icon: Users, label: "Alunos Certificados 2026", value: "183+", accent: SOLAR },
  { icon: Flag, label: "Mercados CPLP", value: "6 Países", accent: CYAN },
];

const ASSETS = [
  { icon: HannoMark, label: "Monograma HX", desc: "SVG · vetorial · verde/branco", href: null, custom: true },
  { icon: ImageIcon, label: "Hero HannoNexus", desc: "PNG 1440×810 · aérea STP", href: "/hanno/hero-main.png" },
  { icon: ImageIcon, label: "Dashboard BioPintoSTP", desc: "PNG · quinta modelo", href: "/hanno/biopinto/biopinto-hero.png" },
  { icon: ImageIcon, label: "Dashboard HX Cabo Verde", desc: "PNG · hub energético", href: "/hanno/cabo-verde/cv-hero.png" },
  { icon: Flag, label: "Bandeira Cabo Verde", desc: "SVG oficial 3:2", href: "/hanno/flags/cabo-verde.svg", flag: "cabo-verde" },
  { icon: Flag, label: "Bandeira São Tomé", desc: "SVG oficial 2:1", href: "/hanno/flags/sao-tome.svg", flag: "sao-tome" },
];

const BIOS = {
  pt: "A HannoNexus é uma plataforma integrada de infraestrutura desenhada e operada pela BSGOTECH Infrastructure Systems. Cobre cinco camadas — energia renovável, mobilidade elétrica, sistemas digitais, infraestrutura física e formação técnica — acelerando o desenvolvimento sustentável dos países lusófonos (CPLP). Projetos-piloto ativos em São Tomé e Príncipe (agricultura de precisão) e Cabo Verde (independência energética arquipelágica).",
  en: "HannoNexus is an integrated infrastructure platform designed and operated by BSGOTECH Infrastructure Systems. It spans five layers — renewable energy, electric mobility, digital systems, physical infrastructure and technical training — accelerating sustainable development across the Portuguese-speaking countries (CPLP). Active pilot projects in São Tomé and Príncipe (precision agriculture) and Cape Verde (archipelagic energy independence).",
  de: "HannoNexus ist eine integrierte Infrastrukturplattform, konzipiert und betrieben von BSGOTECH Infrastructure Systems. Sie umfasst fünf Ebenen — erneuerbare Energie, Elektromobilität, digitale Systeme, physische Infrastruktur und technische Ausbildung — und beschleunigt die nachhaltige Entwicklung der portugiesischsprachigen Länder (CPLP). Aktive Pilotprojekte in São Tomé und Príncipe (Präzisionslandwirtschaft) und Kap Verde (archipelagische Energieunabhängigkeit).",
};

export default function PressKitPage() {
  const [copied, setCopied] = React.useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copiado para o clipboard");
    setTimeout(() => setCopied(null), 2000);
  };

  const meetingLink = typeof window !== "undefined" ? `${window.location.origin}/press-kit` : "/press-kit";

  return (
    <div className="App relative bg-hx-bg text-hx-text" data-testid="page-press-kit">
      <Navbar />

      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 hx-grid-bg opacity-[0.14]" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-[0.10]" style={{ background: `radial-gradient(circle, ${NEON} 0%, transparent 70%)` }} />
      </div>

      <main className="relative">
        {/* HERO */}
        <section className="pt-32 lg:pt-40 pb-14 lg:pb-16 border-b border-white/[0.10]" data-testid="press-hero">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: `${NEON}0f`, borderColor: `${NEON}44` }}>
                <FileText size={12} style={{ color: NEON }} strokeWidth={2} />
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: NEON }}>
                  Press & Investor Kit
                </span>
              </div>
              <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                HannoNexus <span style={{ color: NEON }}>—</span> Media & Executive Briefing
              </h1>
              <p className="mt-5 max-w-3xl text-base lg:text-lg text-hx-muted leading-relaxed">
                Página oficial de recursos para jornalistas, investidores institucionais, parceiros estratégicos e representantes governamentais. Todo o conteúdo aqui disponível pode ser utilizado em publicações, apresentações executivas e comunicações oficiais mediante crédito à <span className="text-hx-text font-semibold">HannoNexus · BSGOTECH Infrastructure Systems</span>.
              </p>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => copyToClipboard(meetingLink, "url")}
                  data-testid="copy-meeting-link"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-[11px] uppercase tracking-[0.2em] transition-all hover:translate-y-[-2px]"
                  style={{ background: NEON, color: "#0A0F1A", boxShadow: `0 0 24px -4px ${NEON}` }}
                >
                  {copied === "url" ? <><CheckCheck size={14} /> Link copiado</> : <><Copy size={14} /> Copiar link da reunião</>}
                </button>
                <a href="#assets" className="text-[11px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2">
                  Descarregar assets <ArrowRight size={12} />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FACT SHEET */}
        <section className="py-14 lg:py-20 border-b border-white/[0.10]" data-testid="fact-sheet">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: NEON }}>Fact Sheet · Números-Chave</div>
            <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight">Métricas operacionais 2026</h2>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
              {FACT_SHEET.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="hx-glass rounded-xl p-5 border"
                  style={{ borderColor: `${f.accent}22` }}
                >
                  <div className="w-8 h-8 rounded-md flex items-center justify-center mb-3" style={{ background: `${f.accent}14`, border: `1px solid ${f.accent}30` }}>
                    <f.icon size={14} style={{ color: f.accent }} strokeWidth={1.7} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted">{f.label}</div>
                  <div className="mt-2 font-display font-bold text-3xl tabular-nums" style={{ color: f.accent }}>{f.value}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* QUICK LINKS · DASHBOARDS */}
        <section className="py-14 lg:py-20 border-b border-white/[0.10]" data-testid="quick-links">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: CYAN }}>Live Dashboards</div>
            <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight">Provas operacionais ao vivo</h2>
            <p className="mt-3 max-w-2xl text-sm text-hx-muted">Abra estes dashboards durante a reunião para demonstrar a operação em tempo real.</p>

            <div className="mt-8 grid md:grid-cols-2 gap-5">
              <QuickLink
                to="/projects/biopintostp"
                flag="sao-tome"
                title="BioPintoSTP · Quinta Modelo"
                sub="São Tomé e Príncipe · Agricultura de precisão"
                accent={NEON}
                testid="link-biopinto"
              />
              <QuickLink
                to="/projects/cabo-verde-hub"
                flag="cabo-verde"
                title="HX Cabo Verde · Hub Energético"
                sub="Cabo Verde · Independência energética insular"
                accent={SOLAR}
                testid="link-cv-hub"
              />
            </div>
          </div>
        </section>

        {/* ASSETS DOWNLOAD */}
        <section id="assets" className="py-14 lg:py-20 border-b border-white/[0.10]" data-testid="assets-section">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: SOLAR }}>Assets Oficiais</div>
            <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight">Logotipos, imagens e bandeiras</h2>
            <p className="mt-3 max-w-2xl text-sm text-hx-muted">Clique com o botão direito → &quot;Guardar como…&quot; para descarregar. Todos os assets são de domínio HannoNexus · BSGOTECH.</p>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {ASSETS.map((a, i) => (
                <div
                  key={i}
                  data-testid={`asset-${i}`}
                  className="hx-glass rounded-xl p-5 border hover:border-hx-neon/40 transition-all group"
                  style={{ borderColor: "rgba(10,155,95,0.15)" }}
                >
                  <div className="aspect-video rounded-lg mb-4 flex items-center justify-center overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    {a.custom ? (
                      <HannoMark size={80} />
                    ) : a.flag ? (
                      <CountryFlag slug={a.flag} className="w-24 h-16 rounded shadow-lg" />
                    ) : (
                      <img src={a.href} alt={a.label} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="text-sm font-display font-semibold text-hx-text">{a.label}</div>
                  <div className="text-xs text-hx-muted mt-0.5">{a.desc}</div>
                  {a.href && (
                    <a
                      href={a.href}
                      download
                      className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-hx-neon hover:text-hx-text transition-colors"
                    >
                      <Download size={11} /> Download
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXECUTIVE BIOS */}
        <section className="py-14 lg:py-20 border-b border-white/[0.10]" data-testid="bios-section">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: NEON }}>Bio Institucional</div>
            <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight">Texto pronto a colar em decks</h2>

            <div className="mt-10 space-y-5">
              {Object.entries(BIOS).map(([lang, text]) => (
                <div key={lang} className="hx-glass rounded-xl p-6 border relative" style={{ borderColor: "rgba(10,155,95,0.15)" }} data-testid={`bio-${lang}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="inline-flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-[0.28em] font-mono font-semibold px-2 py-0.5 rounded border" style={{ background: `${NEON}0d`, borderColor: `${NEON}33`, color: NEON }}>
                        {lang.toUpperCase()}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-hx-muted">
                        {lang === "pt" ? "Português" : lang === "en" ? "English" : "Deutsch"}
                      </span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(text, `bio-${lang}`)}
                      className="text-[10px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-1.5"
                      data-testid={`copy-bio-${lang}`}
                    >
                      {copied === `bio-${lang}` ? <><CheckCheck size={11} /> Copiado</> : <><Copy size={11} /> Copiar</>}
                    </button>
                  </div>
                  <p className="text-sm text-hx-text/90 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACTS */}
        <section className="py-14 lg:py-20" data-testid="contacts-section">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: CYAN }}>Contactos Institucionais</div>
            <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight">Fale connosco</h2>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <ContactCard icon={Mail}     label="Email" value={CONTACTS.email} href={`mailto:${CONTACTS.email}`} accent={NEON} testid="contact-email" />
              <ContactCard icon={Phone}    label="Telefone" value={CONTACTS.phone} href={`tel:${CONTACTS.phone.replace(/\s/g, "")}`} accent={SOLAR} testid="contact-phone" />
              <ContactCard icon={Linkedin} label="LinkedIn" value="BSGOTECH · HannoNexus" href={CONTACTS.linkedin} accent={CYAN} external testid="contact-linkedin" />
            </div>

            <div className="mt-12 hx-glass-strong rounded-2xl p-8 lg:p-10 border text-center" style={{ borderColor: `${NEON}30` }}>
              <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: NEON }}>Reunião Executiva</div>
              <h3 className="mt-3 font-display font-bold text-2xl lg:text-3xl tracking-tight">
                Agendemos uma conversa estruturada de 30 minutos.
              </h3>
              <p className="mt-3 text-sm text-hx-muted max-w-xl mx-auto">
                Investidores, bancos multilaterais, governos e parceiros estratégicos — resposta garantida em 48 horas úteis.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/contact"
                  data-testid="press-cta-contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[11px] uppercase tracking-[0.2em] transition-all hover:translate-y-[-2px]"
                  style={{ background: NEON, color: "#0A0F1A", boxShadow: `0 0 24px -4px ${NEON}` }}
                >
                  <Calendar size={13} /> Solicitar Reunião
                </Link>
                <a
                  href={`mailto:${CONTACTS.email}?subject=HannoNexus%20-%20Executive%20Briefing`}
                  className="text-[11px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2"
                >
                  Enviar email direto <ArrowRight size={12} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ============================================================================
//  Sub-components
// ============================================================================
const QuickLink = ({ to, flag, title, sub, accent, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="hx-glass rounded-2xl p-6 border transition-all hover:-translate-y-1 group flex items-center gap-5"
    style={{ borderColor: `${accent}30` }}
  >
    <CountryFlag slug={flag} className="w-16 h-11 rounded shadow-lg shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }} />
          <span className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: accent }}>Live</span>
        </span>
      </div>
      <div className="mt-1 font-display font-semibold text-lg tracking-tight text-hx-text">{title}</div>
      <div className="text-xs text-hx-muted">{sub}</div>
    </div>
    <ArrowRight size={16} className="text-hx-muted group-hover:text-hx-neon transition-colors shrink-0" />
  </Link>
);

const ContactCard = ({ icon: Icon, label, value, href, accent, external, testid }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    data-testid={testid}
    className="hx-glass rounded-xl p-5 border hover:border-hx-neon/40 transition-all inline-block"
    style={{ borderColor: `${accent}22` }}
  >
    <div className="w-9 h-9 rounded-md flex items-center justify-center mb-3" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
      <Icon size={16} style={{ color: accent }} strokeWidth={1.7} />
    </div>
    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{label}</div>
    <div className="mt-1 text-sm font-semibold text-hx-text break-all">{value}</div>
  </a>
);
